// frontend/src/pages/MyDamageReports.jsx
//
// Liste des signalements de dégâts du farmer connecté + bouton pour fermer.

import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Mes signalements de dégâts',
    subtitle: 'Suivez les signalements et fermez-les une fois résolus.',
    newReport: '+ Nouveau signalement',
    empty: 'Aucun signalement pour le moment.',
    species: 'Espèce',
    damage: 'Dégâts',
    status: 'Statut',
    opened: 'Ouvert',
    statuses: { open: 'Ouvert', matched: 'En traitement', closed: 'Fermé' },
    close: 'Fermer',
    closing: 'Fermeture…',
    confirmClose: 'Fermer ce signalement ? (action définitive)',
    photos: 'photos',
  },
  en: {
    title: 'My damage reports',
    subtitle: 'Track reports and close them once resolved.',
    newReport: '+ New report',
    empty: 'No reports yet.',
    species: 'Species',
    damage: 'Damage',
    status: 'Status',
    opened: 'Opened',
    statuses: { open: 'Open', matched: 'In progress', closed: 'Closed' },
    close: 'Close',
    closing: 'Closing…',
    confirmClose: 'Close this report? (permanent)',
    photos: 'photos',
  },
};

const STATUS_COLORS = {
  open: '#22c55e',
  matched: '#f59e0b',
  closed: 'rgba(255,255,255,0.4)',
};

export default function MyDamageReports() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/depredation/mes-reports');
      setReports(r.data.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeReport = async (id) => {
    if (!window.confirm(t.confirmClose)) return;
    setClosingId(id);
    try {
      await api.put(`/depredation/reports/${id}/close`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur lors de la fermeture.');
    } finally {
      setClosingId(null);
    }
  };

  return (
    <PrivateLayout>
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem' }}>
        <PageHeader icon="🌾" title={t.title} subtitle={t.subtitle} />

        <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
          <Link
            to="/submit-damage-report"
            style={{
              display: 'inline-block',
              padding: '0.7rem 1.2rem',
              background: 'var(--couleur-or)',
              color: '#000',
              borderRadius: '6px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            {t.newReport}
          </Link>
        </div>

        {error && <div style={{ color: '#ff6b6b', marginBottom: '1rem' }}>{error}</div>}

        {loading ? (
          <div style={{ color: 'rgba(255,255,255,0.5)' }}>…</div>
        ) : reports.length === 0 ? (
          <div style={{ color: 'rgba(255,255,255,0.5)', padding: '2rem', textAlign: 'center' }}>
            {t.empty}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {reports.map((r) => (
              <div
                key={r.id}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    {r.species} — {t.damageType?.[r.damage_type] || r.damage_type}
                  </div>
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: 'rgba(255,255,255,0.65)',
                      marginBottom: '0.3rem',
                    }}
                  >
                    {r.description}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)' }}>
                    {t.opened} :{' '}
                    {new Date(r.opened_at).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
                    {' · '}
                    {r.lat?.toFixed(4)}, {r.lng?.toFixed(4)}
                    {r.photos?.length > 0 && ` · ${r.photos.length} ${t.photos}`}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '0.4rem',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: STATUS_COLORS[r.status] || '#fff',
                    }}
                  >
                    {t.statuses[r.status] || r.status}
                  </span>
                  {r.status !== 'closed' && (
                    <button
                      onClick={() => closeReport(r.id)}
                      disabled={closingId === r.id}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: 'transparent',
                        color: 'rgba(255,255,255,0.7)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 4,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                      }}
                    >
                      {closingId === r.id ? t.closing : t.close}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </PrivateLayout>
  );
}
